import { FC, useState } from 'react';

import { useHistory } from 'react-router';
import { addBalanceReplenishmentParamToUrl } from 'utils/balance/balanceReplenishment';
import { formatCost } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import dashboardMultiActionsIncreaseBudgetModalShown from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_increase_budget_modal_shown';
import {
    ActionBar,
    BottomSheet,
    BottomSheetFooter,
    Button,
    FormHelper,
    Modal,
    NavigationBar,
    NumberInput,
    Text,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import batchIncreaseBudget from 'components/Dashboard/MultiActions/BudgetIncrease/batchIncreaseBudget';

import { useDispatch, useSelector } from 'store/utils';

interface BudgetIncreaseModalProps {
    isVisible: boolean;
    hideModal: () => void;
    availableCampaignIdsForBudget: Record<number, boolean>;
    campaignsForBudgetIncreaseCount: number;
}

const TrlKeys = {
    title: 'Dashboard.BudgetIncreaseModal.title',
    text: 'Dashboard.BudgetIncreaseModal.text',
    placeholder: 'Dashboard.BudgetIncreaseModal.input.placeholder',
    confirmButton: 'Dashboard.BudgetIncreaseModal.button.confirm',
    cancelButton: 'Dashboard.BudgetIncreaseModal.button.cancel',
    total: 'Dashboard.BudgetIncreaseModal.total',
    balance: 'Dashboard.BudgetIncreaseModal.balance',
    currency: 'currency.short',
    error: {
        minBudgetValue: 'Dashboard.BudgetIncreaseModal.error.minBudgetValue',
        maxBudgetValue: 'Dashboard.BudgetIncreaseModal.error.maxBudgetValue',
    },
};

const MIN_BUDGET = 500;
const MAX_BUDGET = 100000;

const BudgetIncreaseModal: FC<BudgetIncreaseModalProps> = ({
    isVisible,
    hideModal,
    availableCampaignIdsForBudget,
    campaignsForBudgetIncreaseCount,
}) => {
    const trl = useTrls();
    const dispatch = useDispatch();
    const history = useHistory();

    const realBalance = useSelector((state) => state.balance?.realBalance);

    const [isLoading, setIsLoadingOn, setIsLoadingOff] = useOnOffState(false);
    const [budgetValue, setBudgetValue] = useState('');
    const [validateError, setValidateError] = useState('');

    const total = budgetValue ? Number(budgetValue) * campaignsForBudgetIncreaseCount : 0;

    const validateBudget = (): boolean => {
        if (!budgetValue || Number(budgetValue) < MIN_BUDGET) {
            setValidateError(trl(TrlKeys.error.minBudgetValue, [MIN_BUDGET]));
            return false;
        }
        if (Number(budgetValue) > MAX_BUDGET) {
            setValidateError(trl(TrlKeys.error.maxBudgetValue, [MAX_BUDGET]));
            return false;
        }
        setValidateError('');
        return true;
    };

    const onConfirm = () => {
        const isValid = validateBudget();
        if (!isValid) return;
        setIsLoadingOn();
        dispatch(batchIncreaseBudget(availableCampaignIdsForBudget, budgetValue)).finally(() => {
            setIsLoadingOff();
            hideModal();
            addBalanceReplenishmentParamToUrl(history);
        });
    };

    const confirmButton = (
        <Button
            data-qa="increase-budget-confirm-button"
            mode="primary"
            style="accent"
            onClick={onConfirm}
            size="medium"
            key="increase-budget-confirm"
            loading={isLoading}
        >
            {trl(TrlKeys.confirmButton)}
        </Button>
    );
    const cancelButton = (
        <Button
            data-qa="increase-budget-cancel-button"
            onClick={hideModal}
            mode="secondary"
            style="accent"
            size="medium"
            key="increase-budget-cancel"
            loading={isLoading}
        >
            {trl(TrlKeys.cancelButton)}
        </Button>
    );

    const additionalContent = (
        <div>
            <Text typography="subtitle-1-semibold" style="primary">
                {trl(TrlKeys.total, [formatCost(total, NON_BREAKING_SPACE)])}
            </Text>
            <VSpacing default={6} />
            <Text typography="label-3-regular" style="secondary">
                {trl(TrlKeys.balance, [formatCost(realBalance, NON_BREAKING_SPACE)])}
            </Text>
        </div>
    );

    const content = (
        <div>
            <Text typography="label-2-regular" style="secondary">
                {trl(TrlKeys.text)}
            </Text>
            <VSpacing default={12} />
            <NumberInput
                value={budgetValue}
                onChange={setBudgetValue}
                allowNegative={false}
                decimalMark={[]}
                decimalLength={0}
                data-qa="budget-increase-input"
                placeholder={trl(TrlKeys.placeholder)}
                postfix={trl(TrlKeys.currency)}
                size="medium"
                onBlur={validateBudget}
                invalid={!!validateError}
            />
            <FormHelper
                invalid={!!validateError}
                errorMessage={<div data-qa="budget-increase-input-validate-error">{validateError}</div>}
                topSpacing={8}
            />
            <VSpacing default={8} />
        </div>
    );

    return (
        <ElementShownAnchor fn={dashboardMultiActionsIncreaseBudgetModalShown}>
            <Modal
                size="small"
                visible={isVisible}
                onClose={hideModal}
                header={
                    <MagritteModalHeader
                        title={trl(TrlKeys.title)}
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={hideModal} />}
                    />
                }
                footer={
                    <ActionBar primaryActions={[cancelButton, confirmButton]} additionalContent={additionalContent} />
                }
                data-qa="budget-increase-modal"
            >
                {content}
            </Modal>
            <BottomSheet
                height="content"
                visible={isVisible}
                onClose={hideModal}
                header={
                    <NavigationBar
                        title={trl(TrlKeys.title)}
                        right={<CrossOutlinedSize24 initialColor="secondary" onClick={hideModal} />}
                    />
                }
                footer={
                    <BottomSheetFooter>
                        {confirmButton}
                        {cancelButton}
                    </BottomSheetFooter>
                }
                data-qa="budget-increase-bottom-sheet"
            >
                {content}
                <VSpacing default={16} />
                {additionalContent}
            </BottomSheet>
        </ElementShownAnchor>
    );
};

export default BudgetIncreaseModal;
